import { Typography } from '@material-ui/core';
import { Link } from 'gatsby';
import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { AiVisibilityAccessDialog } from '../../components/AiVisibilityAccessDialog';
import { Headline } from '../../components/Headline';
import { Kicker } from '../../components/Kicker';
import { Reveal } from '../../components/motion';
import {
  ComingSoonBadge,
  NewBadge,
  NoCodeBadge
} from '../../components/NewBadge';
import PageHead from '../../components/PageHead';
import { SEO } from '../../components/SEO';
import { SignupButtonWithChoice } from '../../components/SignupButton';
import MainLayout from '../../layouts/main';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const TiersGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const TierCard = styled('div')<{ dimmed?: boolean }>`
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  opacity: ${(p) => (p.dimmed ? 0.85 : 1)};
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(24, 144, 255, 0.12);
    transform: translateY(-2px);
  }
`;

const TierBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TierTitle = styled('h3')`
  font-size: 22px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const TierDescription = styled('p')`
  font-size: 15px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
`;

const TierSetup = styled('div')`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
`;

const TierList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const TierItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const TierAction = styled('div')`
  margin-top: 8px;
`;

const RequestAccessLink = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const VisibilityLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const LifecycleSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const LifecycleGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 48px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const LifecycleStep = styled('div')`
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 24px;

  .step {
    font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    margin-bottom: 10px;
  }

  h3 {
    font-size: 17px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    line-height: 1.6;
    color: #4f4f4f;
    margin: 0;
  }
`;

const YieldSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const YieldContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const YieldBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
`;

const Screenshot = styled('img')`
  width: 100%;
  height: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 40px rgba(14, 28, 43, 0.1);
`;

const CtaSection = styled('div')`
  text-align: center;
  margin: 0 auto 120px;
  max-width: 700px;
  padding: 0 24px;
`;

export default () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/product/amplify/"
          title="Affilimate Amplify: Campaign Infrastructure for Publishers"
          description="Sponsored content campaigns, agentic link swap campaigns, and soon AI visibility campaigns. Draft, review, launch, and measure on one platform, without adding headcount."
        />
        <PageHead style={{ marginBottom: 0 }}>
          <Reveal>
            <Kicker>Amplify · The campaigns solution</Kicker>
            <Headline
              variant="h1"
              gutterBottom
              component="h1"
              style={{
                maxWidth: '800px',
                margin: '0 auto 24px',
                fontWeight: 900,
                display: 'block'
              }}
            >
              Campaign infrastructure that works the way your team does{' '}
              <NewBadge>New</NewBadge>
            </Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ maxWidth: '620px', margin: '24px auto 48px' }}
            >
              Sell and run sponsorship campaigns across your entire portfolio
              without adding headcount. AI finds the opportunities and drafts
              the campaigns. Your team reviews, launches, and measures, all in
              one place.
            </Typography>
          </Reveal>
        </PageHead>

        <TiersGrid>
          <Reveal>
            <TierCard>
              <TierBadgeRow>
                <NoCodeBadge />
              </TierBadgeRow>
              <TierTitle>Sponsored content campaigns</TierTitle>
              <TierSetup>Live today · No dev work required</TierSetup>
              <TierDescription>
                Package new content, inclusions, and placements into
                sponsorships brands fund, with performance tracking built in
                from day one.
              </TierDescription>
              <TierList>
                <TierItem>
                  <Check size={16} />
                  Offer campaigns on a CPA, CPC, or flat spend basis
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Draft campaigns from chat with the Agent
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Measure profitability per campaign, page, and link
                </TierItem>
              </TierList>
            </TierCard>
          </Reveal>
          <Reveal delay={120}>
            <TierCard>
              <TierTitle style={{ marginTop: 30 }}>
                Agentic link swap campaigns
              </TierTitle>
              <TierSetup>Live today · One-time on-page integration</TierSetup>
              <TierDescription>
                Reroute existing links to higher-yield merchants across your
                whole portfolio, scoped by AI, approved by you, and reverted
                automatically when the campaign ends.
              </TierDescription>
              <TierList>
                <TierItem>
                  <Check size={16} />
                  Cross-merchant swaps at product and category level
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Automated link swaps, not manual edits
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Auto-revert on goal completion or cutoff date
                </TierItem>
              </TierList>
            </TierCard>
          </Reveal>
          <Reveal delay={240}>
            <TierCard dimmed>
              <TierBadgeRow>
                <ComingSoonBadge />
              </TierBadgeRow>
              <TierTitle>AI visibility campaigns</TierTitle>
              <TierSetup>Coming soon · Early access open</TierSetup>
              <TierDescription>
                Monetize the influence you already have in AI answers. Discover
                opportunities, pitch brands sellable packages, and execute with
                a team checklist.
              </TierDescription>
              <TierList>
                <TierItem>
                  <Check size={16} />
                  Built for publishers, not brand monitoring
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Offers shaped by your editorial practices
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Execution and influence tracking included
                </TierItem>
              </TierList>
              <TierAction>
                <VisibilityLink to="/ai-visibility/">
                  Learn more <ArrowRight size={16} />
                </VisibilityLink>
                {' · '}
                <RequestAccessLink onClick={() => setDialogOpen(true)}>
                  Request early access
                </RequestAccessLink>
              </TierAction>
            </TierCard>
          </Reveal>
        </TiersGrid>

        <LifecycleSection>
          <Reveal>
            <div style={{ textAlign: 'center' }}>
              <Headline component="h2">
                Draft → Review → Launch → Measure
              </Headline>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ maxWidth: '620px', margin: '16px auto 0' }}
              >
                Every campaign type runs through the same lifecycle, so your
                team learns one workflow and AI accelerates every step of it.
              </Typography>
            </div>
          </Reveal>
          <LifecycleGrid>
            <Reveal>
              <LifecycleStep>
                <div className="step">01 · Draft</div>
                <h3>Opportunities become drafts</h3>
                <p>
                  Yield Optimizer and the Agent turn portfolio analysis into
                  ready-to-review campaign drafts, scoped to relevant pages.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={100}>
              <LifecycleStep>
                <div className="step">02 · Review</div>
                <h3>Your team stays in control</h3>
                <p>
                  Review pages, terms, and projected lift. Adjust anything.
                  Nothing goes live without approval.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={200}>
              <LifecycleStep>
                <div className="step">03 · Launch</div>
                <h3>Execution is automated</h3>
                <p>
                  Links deploy across your portfolio automatically. No manual
                  edits, no CMS wrangling, no engineering tickets.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={300}>
              <LifecycleStep>
                <div className="step">04 · Measure</div>
                <h3>Profitability, live</h3>
                <p>
                  Track campaign performance in real time and adjust
                  mid-flight, with link-level attribution throughout.
                </p>
              </LifecycleStep>
            </Reveal>
          </LifecycleGrid>
        </LifecycleSection>

        <YieldSection>
          <Reveal>
            <YieldContent>
              <Kicker style={{ marginBottom: 0 }}>Yield Optimizer</Kicker>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Find the money you're leaving on the table
              </Headline>
              <YieldBody>
                Yield Optimizer analyzes your catalog-linked inventory for
                cross-merchant opportunities, and puts AI-suggested campaign
                drafts at the top of the page with estimated lift, page
                counts, and the reasoning behind each one.
              </YieldBody>
              <YieldBody>
                Match existing product links against 100+ merchant feeds and
                over 500 million products, then act on the spread in minutes
                instead of quarters.
              </YieldBody>
            </YieldContent>
          </Reveal>
          <Reveal delay={150}>
            <Screenshot
              src="/images/screenshots/prism-opp-matrix.png"
              alt="Yield Optimizer opportunity matrix"
            />
          </Reveal>
        </YieldSection>

        <YieldSection>
          <Reveal>
            <Screenshot
              src="/images/screenshots/prism-campaign-management.png"
              alt="Campaign management platform"
            />
          </Reveal>
          <Reveal delay={150}>
            <YieldContent>
              <Kicker style={{ marginBottom: 0 }}>Campaign management</Kicker>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Built for lean commerce teams
              </Headline>
              <YieldBody>
                Our customers run commerce businesses without an army of
                analysts. Amplify replaces hours of cross-team effort (digging
                through analytics, identifying links in content, comparing
                advertisers, proposing campaigns) with one streamlined
                workflow your existing team can own.
              </YieldBody>
            </YieldContent>
          </Reveal>
        </YieldSection>

        <CtaSection>
          <Reveal>
            <Headline component="h2">
              See Amplify on your own inventory
            </Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ margin: '16px auto 32px' }}
            >
              We'll show you real opportunities from your catalog-linked
              inventory on the first call.
            </Typography>
            <SignupButtonWithChoice text="Book a demo" />
          </Reveal>
        </CtaSection>

        <AiVisibilityAccessDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      </div>
    </MainLayout>
  );
};
